import { useCallback } from 'react';

const useAudioControls = ({
  soundRef,
  setIsPlaying,
  duration,
  setProgress,
  volume,
  setVolume,
  setPlaybackRate,
  setAccessibleProgressText,
  setAccessibleVolumeText,
}) => {

  const formatTimeAccessible = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'} y ${remainingSeconds} ${
      remainingSeconds === 1 ? 'segundo' : 'segundos'
    }`;
  }, []);


  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }, []);

  const handlePlay = useCallback(() => {
    soundRef.current?.play();
    setIsPlaying(true);
  }, [soundRef, setIsPlaying]);

  const handlePause = useCallback(() => {
    soundRef.current?.pause();
    setIsPlaying(false);
  }, [soundRef, setIsPlaying]);

  const handleSeek = useCallback((e) => {
    const seekTime = (e.target.value / 100) * duration;

    if (soundRef.current) {
      soundRef.current.seek(seekTime);
      setProgress(seekTime);

      const currentFormatted = formatTimeAccessible(seekTime);
      const totalFormatted = formatTimeAccessible(duration);
      setAccessibleProgressText(`Tiempo actual: ${currentFormatted} de ${totalFormatted}`);
    }
  }, [duration, soundRef, setProgress, formatTimeAccessible, setAccessibleProgressText]);

  const increaseVolume = useCallback(() => {
    const newVolume = Math.min(1, volume + 0.1);
    setVolume(newVolume);
  
    const volumePercentage = Math.round(newVolume * 100);
    setAccessibleVolumeText(`Volumen: ${volumePercentage}%`);
  
    if (soundRef.current) {
      soundRef.current.volume(newVolume);
    }
  }, [volume, setVolume, soundRef]);
  
  const decreaseVolume = useCallback(() => {
    const newVolume = Math.max(0, volume - 0.1);
    setVolume(newVolume);
    const volumePercentage = Math.round(newVolume * 100);
    setAccessibleVolumeText(`Volumen: ${volumePercentage}%`);
  
    if (soundRef.current) {
      soundRef.current.volume(newVolume);
    }
  }, [volume, setVolume, soundRef]);

  const changeSpeed = useCallback((speed) => {
    if (soundRef.current) {
      soundRef.current.rate(speed);
    }
    setPlaybackRate(speed);
  }, [soundRef, setPlaybackRate]);

  const handleSeekBackward = (seconds) => {
    if (soundRef.current) {
      const newTime = Math.max(0, soundRef.current.seek() - seconds);
      soundRef.current.seek(newTime);
      setProgress(newTime);
    }
  };
  
  const handleSeekForward = (seconds) => {
    if (soundRef.current) {
      const newTime = Math.min(duration, soundRef.current.seek() + seconds);
      soundRef.current.seek(newTime);
      setProgress(newTime);
    }
  };

  return {
    formatTime,
    handlePlay,
    handlePause,
    handleSeek,
    increaseVolume,
    decreaseVolume,
    changeSpeed,
    handleSeekBackward,
    handleSeekForward,
  };
};

export default useAudioControls;
